import { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import {
  Fingerprint,
  Wifi,
  RefreshCw,
  Save,
  CheckCircle2,
  AlertCircle,
  Search,
  Server,
  Users,
  History,
  Calendar
} from 'lucide-react';
import { Modal } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Spinner } from '@/shared/components/ui/Spinner';
import { Table, type Column } from '@/shared/components/ui/Table';
import { api, getApiErrorMessage } from '@/shared/api/axios';
import { BiometricDateWiseCard } from './BiometricDateWiseCard';

interface BiometricSettingsModalProps {
  open: boolean;
  onClose: () => void;
}

interface BiometricConfig {
  name?: string;
  ip_address?: string;
  subnet_mask?: string;
  port?: number;
  sync_mode?: string;
  is_active?: boolean;
  last_sync_at?: string;
  last_sync_status?: string;
  last_sync_message?: string;
  total_punches_received?: number;
}

interface UserMapping {
  id: string;
  display_name: string;
  department: string;
  role: string;
  biometric_id: string;
}

interface RawPunch {
  id: string;
  display_name: string;
  department: string;
  biometric_id: string;
  punch_time: string;
  punch_mode: number;
  device_ip: string;
}

export function BiometricSettingsModal({ open, onClose }: BiometricSettingsModalProps) {
  const [activeTab, setActiveTab] = useState<'report' | 'config' | 'mappings' | 'audit'>('report');

  // Config State
  const [config, setConfig] = useState<BiometricConfig>({
    name: 'Office eSSL Machine',
    ip_address: '192.0.2.10',
    subnet_mask: '255.255.255.0',
    port: 4370,
    sync_mode: 'bridge_agent',
  });
  const [configLoading, setConfigLoading] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);
  const [testResult, setTestResult] = useState<{ ok?: boolean; message?: string } | null>(null);
  const [testing, setTesting] = useState(false);

  // Mappings State
  const [mappings, setMappings] = useState<UserMapping[]>([]);
  const [mappingsLoading, setMappingsLoading] = useState(false);
  const [mappingsSaving, setMappingsSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Audit State
  const [punches, setPunches] = useState<RawPunch[]>([]);
  const [punchesLoading, setPunchesLoading] = useState(false);

  // Load data when modal opens or tab switches
  useEffect(() => {
    if (!open) return;
    if (activeTab === 'config') loadConfig();
    if (activeTab === 'mappings') loadMappings();
    if (activeTab === 'audit') loadPunches();
  }, [open, activeTab]);

  const loadConfig = async () => {
    try {
      setConfigLoading(true);
      const { data } = await api.get<{ config: BiometricConfig }>('/api/attendance/biometric/config');
      if (data.config) {
        setConfig(prev => ({ ...prev, ...data.config }));
      }
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to load biometric config.'));
    } finally {
      setConfigLoading(false);
    }
  };

  const loadMappings = async () => {
    try {
      setMappingsLoading(true);
      const { data } = await api.get<{ users: UserMapping[] }>('/api/attendance/biometric/mappings');
      setMappings(data.users || []);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to load employee mappings.'));
    } finally {
      setMappingsLoading(false);
    }
  };

  const loadPunches = async () => {
    try {
      setPunchesLoading(true);
      const { data } = await api.get<{ punches: RawPunch[] }>('/api/attendance/biometric/punches?limit=100');
      setPunches(data.punches || []);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to load audit punches.'));
    } finally {
      setPunchesLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    try {
      setConfigSaving(true);
      await api.put('/api/attendance/biometric/config', config);
      toast.success('Biometric device configuration saved.');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to save configuration.'));
    } finally {
      setConfigSaving(false);
    }
  };

  const handleTestConnection = async () => {
    try {
      setTesting(true);
      setTestResult(null);
      const { data } = await api.post<{ ok: boolean; message: string }>('/api/attendance/biometric/test-connection');
      setTestResult({ ok: data.ok, message: data.message });
      if (data.ok) toast.success('Connection parameters verified.');
    } catch (err) {
      const msg = getApiErrorMessage(err, 'Connection test failed.');
      setTestResult({ ok: false, message: msg });
      toast.error(msg);
    } finally {
      setTesting(false);
    }
  };

  const handleSaveMappings = async () => {
    try {
      setMappingsSaving(true);
      const payload = mappings.map(m => ({
        id: m.id,
        biometric_id: m.biometric_id
      }));
      const { data } = await api.put<{ ok: boolean; updated: number }>('/api/attendance/biometric/mappings', {
        mappings: payload
      });
      toast.success(`Updated ${data.updated} employee mappings.`);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to update employee mappings.'));
    } finally {
      setMappingsSaving(false);
    }
  };

  const filteredMappings = mappings.filter(m => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      m.display_name.toLowerCase().includes(q) ||
      m.department.toLowerCase().includes(q) ||
      m.role.toLowerCase().includes(q) ||
      m.biometric_id.toLowerCase().includes(q)
    );
  });

  const mappingColumns: Column<UserMapping>[] = [
    {
      key: 'display_name',
      header: 'Employee Name',
      render: (item) => (
        <div>
          <div className="font-medium text-on-surface">{item.display_name}</div>
          <div className="text-body-xs text-on-surface-variant capitalize">{item.role}</div>
        </div>
      )
    },
    {
      key: 'department',
      header: 'Department',
      render: (item) => (
        <span className="inline-flex rounded-full bg-surface-variant px-2.5 py-0.5 text-body-xs font-medium text-on-surface-variant capitalize">
          {item.department || 'N/A'}
        </span>
      )
    },
    {
      key: 'biometric_id',
      header: 'eSSL Machine Enroll ID',
      render: (item) => (
        <input
          type="text"
          placeholder="e.g. 101"
          value={item.biometric_id}
          onChange={(e) => {
            const val = e.target.value.trim();
            setMappings(prev =>
              prev.map(p => (p.id === item.id ? { ...p, biometric_id: val } : p))
            );
          }}
          className="w-32 rounded-lg border border-outline bg-surface px-3 py-1.5 text-body-sm text-on-surface focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
        />
      )
    }
  ];

  const punchColumns: Column<RawPunch>[] = [
    {
      key: 'display_name',
      header: 'Employee',
      render: (p) => (
        <div>
          <div className="font-medium text-on-surface">{p.display_name}</div>
          <div className="text-body-xs text-on-surface-variant capitalize">{p.department}</div>
        </div>
      )
    },
    {
      key: 'biometric_id',
      header: 'Enroll ID',
      render: (p) => (
        <span className="font-mono text-body-sm font-semibold text-primary">{p.biometric_id}</span>
      )
    },
    {
      key: 'punch_time',
      header: 'Punch Time (IST)',
      render: (p) => {
        if (!p.punch_time) return '—';
        const d = new Date(p.punch_time);
        return isNaN(d.getTime())
          ? p.punch_time
          : d.toLocaleString('en-IN', {
              timeZone: 'Asia/Kolkata',
              day: '2-digit',
              month: 'short',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
              hour12: true
            });
      }
    },
    {
      key: 'punch_mode',
      header: 'Mode',
      render: (p) => (
        <span className="inline-flex rounded-full bg-primary/10 px-2.5 py-0.5 text-body-xs font-medium text-primary">
          {p.punch_mode === 0 ? 'Check In / Out' : `Mode ${p.punch_mode}`}
        </span>
      )
    },
    {
      key: 'device_ip',
      header: 'Device IP',
      render: (p) => (
        <span className="font-mono text-body-xs text-on-surface-variant">{p.device_ip || '192.0.2.10'}</span>
      )
    }
  ];

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={
        <div className="flex items-center gap-2.5">
          <Fingerprint className="h-5 w-5 text-primary" />
          <span>eSSL Biometric Attendance Integration</span>
        </div>
      }
    >
      <div className="flex flex-col gap-5">
        {/* Navigation Tabs */}
        <div className="flex border-b border-outline overflow-x-auto">
          <button
            type="button"
            onClick={() => setActiveTab('report')}
            className={`flex items-center gap-2 border-b-2 px-4 py-2.5 text-body-sm font-medium whitespace-nowrap transition-colors ${
              activeTab === 'report'
                ? 'border-primary text-primary'
                : 'border-transparent text-on-surface-variant hover:text-on-surface'
            }`}
          >
            <Calendar className="h-4 w-4" />
            Date-Wise Report
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('config')}
            className={`flex items-center gap-2 border-b-2 px-4 py-2.5 text-body-sm font-medium whitespace-nowrap transition-colors ${
              activeTab === 'config'
                ? 'border-primary text-primary'
                : 'border-transparent text-on-surface-variant hover:text-on-surface'
            }`}
          >
            <Server className="h-4 w-4" />
            Device Configuration
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('mappings')}
            className={`flex items-center gap-2 border-b-2 px-4 py-2.5 text-body-sm font-medium whitespace-nowrap transition-colors ${
              activeTab === 'mappings'
                ? 'border-primary text-primary'
                : 'border-transparent text-on-surface-variant hover:text-on-surface'
            }`}
          >
            <Users className="h-4 w-4" />
            Employee ID Mappings
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('audit')}
            className={`flex items-center gap-2 border-b-2 px-4 py-2.5 text-body-sm font-medium whitespace-nowrap transition-colors ${
              activeTab === 'audit'
                ? 'border-primary text-primary'
                : 'border-transparent text-on-surface-variant hover:text-on-surface'
            }`}
          >
            <History className="h-4 w-4" />
            Live Audit & Punches
          </button>
        </div>

        {/* TAB 0: DATE-WISE HARDWARE REPORT */}
        {activeTab === 'report' && <BiometricDateWiseCard inModal={true} />}

        {/* TAB 1: DEVICE CONFIGURATION */}
        {activeTab === 'config' && (
          <div className="flex flex-col gap-4">
            {configLoading ? (
              <div className="flex h-40 items-center justify-center">
                <Spinner />
              </div>
            ) : (
              <>
                <div>
                  <label className="mb-1.5 block text-body-xs font-semibold text-on-surface">
                    Connection mode
                  </label>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {([
                      {
                        id: 'push_adms',
                        title: 'Device push (ADMS)',
                        detail: 'The device connects out to Zynx itself. No office PC, no port forwarding.',
                      },
                      {
                        id: 'bridge_agent',
                        title: 'LAN bridge agent (legacy)',
                        detail: 'An office PC polls the device over the LAN and forwards punches.',
                      },
                    ] as const).map((mode) => (
                      <button
                        key={mode.id}
                        type="button"
                        onClick={() => setConfig({ ...config, sync_mode: mode.id })}
                        className={`rounded-xl border p-3 text-left transition-colors ${
                          (config.sync_mode ?? 'push_adms') === mode.id
                            ? 'border-primary bg-primary/5'
                            : 'border-outline-variant hover:bg-surface-variant/40'
                        }`}
                      >
                        <div className="font-semibold text-on-surface">{mode.title}</div>
                        <p className="mt-1 text-body-xs text-on-surface-variant">{mode.detail}</p>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="rounded-xl border border-primary/20 bg-primary/5 p-4">
                  <div className="flex items-start gap-3">
                    <Wifi className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                    {(config.sync_mode ?? 'push_adms') === 'push_adms' ? (
                      <div>
                        <h4 className="font-semibold text-on-surface">Set this on the device keypad</h4>
                        <pre className="mt-1 overflow-x-auto whitespace-pre-wrap text-body-xs text-on-surface-variant">
{`Menu → Comm. → Cloud Server Setting   (some models: ADMS)
  Enable Domain Name : ON
  Server Address     : ${typeof window !== 'undefined' ? window.location.hostname : 'your-server'}
  Server Port        : ${typeof window !== 'undefined' && window.location.port ? window.location.port : '80'}
  Enable Proxy       : OFF

Then approve the device under Attendance → Biometric Devices.`}
                        </pre>
                      </div>
                    ) : (
                      <div>
                        <h4 className="font-semibold text-on-surface">LAN bridge architecture</h4>
                        <p className="mt-1 text-body-xs text-on-surface-variant">
                          Run <code className="rounded bg-surface px-1.5 py-0.5 font-mono">node zynx-biometric-agent.js</code> on
                          an office PC on the same network as the device
                          (<code className="font-mono font-semibold">{config.ip_address || '192.0.2.10'}</code>).
                          That PC must stay on for punches to reach Zynx.
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="mb-1.5 block text-body-xs font-semibold text-on-surface">
                      Device Name
                    </label>
                    <Input
                      value={config.name || ''}
                      onChange={(e) => setConfig({ ...config, name: e.target.value })}
                      placeholder="Office eSSL Machine"
                    />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-body-xs font-semibold text-on-surface">
                      IP Address (LAN)
                    </label>
                    <Input
                      value={config.ip_address || ''}
                      onChange={(e) => setConfig({ ...config, ip_address: e.target.value })}
                      placeholder="192.0.2.10"
                    />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-body-xs font-semibold text-on-surface">
                      Subnet Mask
                    </label>
                    <Input
                      value={config.subnet_mask || ''}
                      onChange={(e) => setConfig({ ...config, subnet_mask: e.target.value })}
                      placeholder="255.255.255.0"
                    />
                  </div>
                  <div>
                    <label className="mb-1.5 block text-body-xs font-semibold text-on-surface">
                      TCP Comm Port
                    </label>
                    <Input
                      type="number"
                      value={String(config.port || 4370)}
                      onChange={(e) => setConfig({ ...config, port: Number(e.target.value) })}
                      placeholder="4370"
                    />
                  </div>
                </div>

                {testResult && (
                  <div
                    className={`flex items-start gap-3 rounded-xl border p-4 text-body-sm ${
                      testResult.ok
                        ? 'border-success/40 bg-success text-success-text dark:text-success-text'
                        : 'border-danger/40 bg-danger text-danger-text dark:text-danger-text'
                    }`}
                  >
                    {testResult.ok ? (
                      <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" />
                    ) : (
                      <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
                    )}
                    <div>{testResult.message}</div>
                  </div>
                )}

                <div className="mt-2 flex items-center justify-between border-t border-outline pt-4">
                  <Button
                    variant="outline"
                    onClick={handleTestConnection}
                    disabled={testing}
                  >
                    {testing ? 'Testing...' : 'Test Connection'}
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleSaveConfig}
                    disabled={configSaving}
                  >
                    <Save className="mr-2 h-4 w-4" />
                    {configSaving ? 'Saving...' : 'Save Configuration'}
                  </Button>
                </div>
              </>
            )}
          </div>
        )}

        {/* TAB 2: EMPLOYEE ID MAPPINGS */}
        {activeTab === 'mappings' && (
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between gap-4">
              <div className="relative max-w-xs flex-1">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-on-surface-variant" />
                <input
                  type="text"
                  placeholder="Search employees..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full rounded-lg border border-outline bg-surface py-2 pl-9 pr-3 text-body-sm text-on-surface focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>
              <Button
                variant="primary"
                onClick={handleSaveMappings}
                disabled={mappingsSaving}
              >
                <Save className="mr-2 h-4 w-4" />
                {mappingsSaving ? 'Saving...' : 'Save Mappings'}
              </Button>
            </div>

            {mappingsLoading ? (
              <div className="flex h-40 items-center justify-center">
                <Spinner />
              </div>
            ) : (
              <div className="max-h-80 overflow-y-auto rounded-xl border border-outline">
                <Table
                  columns={mappingColumns}
                  data={filteredMappings}
                  rowKey={(m) => m.id}
                  empty="No active employees found."
                />
              </div>
            )}
          </div>
        )}

        {/* TAB 3: LIVE AUDIT & PUNCHES */}
        {activeTab === 'audit' && (
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <p className="text-body-sm text-on-surface-variant">
                Showing most recent raw biometric punches received from the Office LAN Bridge Agent.
              </p>
              <Button variant="outline" size="sm" onClick={loadPunches} disabled={punchesLoading}>
                <RefreshCw className={`mr-2 h-4 w-4 ${punchesLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>

            {punchesLoading ? (
              <div className="flex h-40 items-center justify-center">
                <Spinner />
              </div>
            ) : (
              <div className="max-h-80 overflow-y-auto rounded-xl border border-outline">
                <Table
                  columns={punchColumns}
                  data={punches}
                  rowKey={(p) => p.id}
                  empty="No biometric punches received yet. Run zynx-biometric-agent.js to test."
                />
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
